import { Button } from "@/projects/NPS/Stepper/common/button";
import { ArrowLeft, FileQuestion, HelpCircle, Home } from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const NotFoundPage = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
  }, [location.pathname]);

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-background via-secondary/5 to-primary/5">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute left-20 top-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-20 right-20 h-96 w-96 rounded-full bg-secondary/10 blur-3xl" />
      </div>

      <div className="relative z-10 px-6 text-center">
        {/* Animated 404 */}
        <div className="relative mb-8">
          <div className="flex items-center justify-center gap-2">
            <span className="animate-fade-in bg-gradient-to-br from-primary via-secondary to-primary/60 bg-clip-text font-['Raleway'] text-[120px] font-bold leading-none text-transparent md:text-[180px]">
              4
            </span>
            <div className="relative">
              <FileQuestion className="animate-scale-in h-24 w-24 text-primary/80 md:h-32 md:w-32" />
              <div className="absolute inset-0 rounded-full bg-primary/20 blur-2xl" />
            </div>
            <span
              className="animate-fade-in bg-gradient-to-br from-primary/60 via-secondary to-primary bg-clip-text font-['Raleway'] text-[120px] font-bold leading-none text-transparent md:text-[180px]"
              style={{ animationDelay: "0.2s" }}
            >
              4
            </span>
          </div>
        </div>

        <h1
          className="animate-fade-in mb-4 font-['Raleway'] text-3xl font-bold text-secondary md:text-4xl"
          style={{ animationDelay: "0.3s" }}
        >
          Page Not Found
        </h1>

        <p
          className="animate-fade-in mx-auto mb-8 max-w-md text-lg text-muted-foreground"
          style={{ animationDelay: "0.4s" }}
        >
          Oops! The page you&apos;re looking for seems to have wandered off.
          Let&apos;s get you back on track.
        </p>

        {/* Actions */}
        <div
          className="animate-fade-in flex flex-col items-center justify-center gap-4 sm:flex-row"
          style={{ animationDelay: "0.5s" }}
        >
          <Link to="/">
            <Button
              size="lg"
              className="gap-2 bg-primary shadow-lg shadow-primary/20 hover:bg-primary/90"
            >
              <Home className="h-5 w-5" />
              Go to Homepage
            </Button>
          </Link>
          <Button
            variant="outline"
            size="lg"
            className="gap-2"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-5 w-5" />
            Go Back
          </Button>
        </div>

        {/* Quick Links */}
        <div
          className="animate-fade-in mt-12 border-t border-border/50 pt-8"
          style={{ animationDelay: "0.6s" }}
        >
          <p className="mb-4 text-sm text-muted-foreground">
            Or explore these popular pages:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/ipo"
              className="text-sm font-medium text-primary hover:underline"
            >
              IPO
            </Link>
            <span className="text-border">•</span>
            <Link
              to="/bonds"
              className="text-sm font-medium text-primary hover:underline"
            >
              Bonds
            </Link>
            <span className="text-border">•</span>
            <Link
              to="/fds"
              className="text-sm font-medium text-primary hover:underline"
            >
              Fixed Deposits
            </Link>
            <span className="text-border">•</span>
            <Link
              to="/nps"
              className="text-sm font-medium text-primary hover:underline"
            >
              NPS
            </Link>
            <span className="text-border">•</span>
            <Link
              to="/dashboard"
              className="text-sm font-medium text-primary hover:underline"
            >
              Dashboard
            </Link>
          </div>
        </div>

        {/* Support */}
        <div
          className="animate-fade-in mt-8"
          style={{ animationDelay: "0.7s" }}
        >
          <Link
            to="/dashboard/help"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary"
          >
            <HelpCircle className="h-4 w-4" />
            Need help? Contact Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFoundPage;
